import type { AxiosProgressEvent } from "axios";
import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import { ContentTags } from "~/core/models/ContentTags";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import type { FiltersSelection } from "~/core/models/filters";
import contentService from "~/core/services/content.service";
import RestService from "~/core/services/rest.service";
import uploadUtils from "~/core/utils/upload.utils";
import { CAMPAIGN_PARAMS_SCOPE } from "~/modules/campaign/constants/constants";
import type { DtwinMetricsByModelFriendlyName } from "~/modules/dashboard/models/DtwinMetricsByModelFriendlyName.type";
import type { DtwinMetricsCounts } from "~/modules/dashboard/models/DtwinMetricsCounts.type";
import type { DtwinsMetricsCertificate } from "~/modules/dashboard/models/DtwinsMetricsCertificate.interface";
import { COMPONENT_ID_BS_TRANSLATION_UI_CONFIG } from "~/modules/dtwin/constants";
import type { Dtwin } from "~/modules/dtwin/models/Dtwin.interface";
import type { DtwinAddConfigurationPayload } from "~/modules/dtwin/models/DtwinAddConfigurationPayload.interface";
import type { DtwinConfigurationCreateContentResourceResponse } from "~/modules/dtwin/models/DtwinConfigurationCreateContentResourceResponse.interface";
import type { DtwinConfigurationUploadPayloadCreateContentResource } from "~/modules/dtwin/models/DtwinConfigurationUploadPayloadCreateContentResource.interface";
import type { DtwinDistinctValues } from "~/modules/dtwin/models/DtwinDistinctValues.interface";
import type { DtwinExistingConnectivityStatus } from "~/modules/dtwin/models/DtwinExistingConnectivityStatus.interface";
import type { DtwinImportMode } from "~/modules/dtwin/models/DtwinImportMode.enum";
import type { DtwinImportResults } from "~/modules/dtwin/models/DtwinImportResults.interface";
import type { DtwinLifeCycleState } from "~/modules/dtwin/models/DtwinLifeCycleState.enum";
import type { DtwinModel } from "~/modules/dtwin/models/DtwinModel.interface";
import type { DtwinModelUiConfig } from "~/modules/dtwin/models/DtwinModelUiConfig.interface";
import organizationService from "~/modules/organization/services/organization.service";

export class DtwinService extends RestService {
  protected DTWINS = "dtwins";
  protected METRICS = "metrics";

  /**
   * Retrieve an organization device model by its ID
   * @param organizationCode
   * @param id
   * @returns
   */
  getOrganizationDtwinsModelById(
    organizationCode: number,
    id: string,
  ): Promise<DtwinModel> {
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/models/${id}`,
    );
  }

  /**
   * Fetch organization devices
   * @param organizationCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param params
   */
  async getOrganizationDtwins({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<Dtwin, DtwinDistinctValues>
  > {
    let filterWithCompatibilityCriteria = searchParams as string;

    const compatibilityCriteria = params?.get("compatibilityCriteria");
    if (compatibilityCriteria) {
      filterWithCompatibilityCriteria += `${filterWithCompatibilityCriteria ? ";" : ""}${compatibilityCriteria}`;
    }

    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}`,
      {
        params: {
          page: page.toString(),
          pageSize: pageSize.toString(),
          filter: filterWithCompatibilityCriteria,
          sort,
          showActionInProgress: params?.get("showActionInProgress"),
          distinctValues: params?.get("distinctValues"),
          withinCampaignScope: params?.get(CAMPAIGN_PARAMS_SCOPE),
        },
      },
    );
  }

  /**
   * Fetch organization sector devices
   * @param organizationCode
   * @param sectorCode
   * @param page
   * @param pageSize
   * @param searchParams
   * @param sort
   * @param params
   */
  async getOrganizationSectorDtwins({
    organizationCode,
    sectorCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<
    PaginatedList<Dtwin, DtwinDistinctValues>
  > {
    let filterWithCompatibilityCriteria = searchParams as string;

    const compatibilityCriteria = params?.get("compatibilityCriteria");
    if (compatibilityCriteria) {
      filterWithCompatibilityCriteria += `${filterWithCompatibilityCriteria ? ";" : ""}${compatibilityCriteria}`;
    }

    return this.$apiV2.get(
      `${this.getOrganizationSectorDtwinsPath(organizationCode, sectorCode)}`,
      {
        params: {
          page: page.toString(),
          pageSize: pageSize.toString(),
          filter: filterWithCompatibilityCriteria,
          sort,
          distinctValues: params?.get("distinctValues"),
        },
      },
    );
  }

  /**
   * Fetch organization device types
   * @param organizationCode
   * @returns
   */
  async getOrganizationAllDtwinModels(
    organizationCode?: number,
  ): Promise<DtwinModel[]> {
    return this.$apiV2.get(
      this.getOrganizationDtwinsPath(organizationCode) + "/models",
    );
  }

  /**
   * Retrieve an organization device
   * @param organizationCode
   * @param id
   * @param showMetadata If set to true, ask the dtwin to add the device metadata. Default: false.
   * @param addOperationStatuses If set true, AS will add last operations statuses to the Dtwin object.
   */
  getOrganizationDtwin(
    organizationCode: number,
    id: string,
    showMetadata?: boolean,
    addOperationStatuses?: boolean,
  ): Promise<Dtwin> {
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${id}`,
      {
        params: {
          showMetadata,
          addOperationStatuses,
        },
      },
    );
  }

  getDtwinsConnectivityStatus(
    organizationCode: number,
  ): Promise<DtwinExistingConnectivityStatus> {
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/existingConnectivityStatus`,
    );
  }

  /**
   * Update device properties
   * @param organizationCode
   * @param uid
   * @param data
   */
  patchOrganizationDtwin({
    organizationCode,
    uid,
    data,
  }: {
    organizationCode?: number;
    uid: string;
    data: Record<string, unknown>;
  }): Promise<void> {
    return this.$apiV2.patch(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${uid}/attributes`,
      data,
    );
  }

  /**
   * Import devices to an organization
   * @param organizationCode
   * @param file
   * @param progress
   * @param analyze
   */
  importOrganizationDtwins(
    organizationCode: number,
    file: File,
    progress: Ref<number | undefined | null>,
    analyze: DtwinImportMode,
  ): Promise<DtwinImportResults> {
    const formData = new FormData();
    formData.append("file", file);
    return this.$apiV2.post(
      `${this.getOrganizationDtwinsPath(organizationCode)}/import?mode=${analyze}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          progress.value = progressEvent.total
            ? (progressEvent.loaded / progressEvent.total) * 100
            : 0;
        },
        timeout: Infinity,
      },
    );
  }

  /**
   * Retrieve device configuration
   * @param organizationCode
   * @param id
   * @returns
   */
  getOrganizationDtwinTelemetries(organizationCode: number, id: string) {
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${id}/dataviz/configuration`,
    );
  }

  getOrganizationDtwinTelemetryValues(
    organizationCode: number,
    id: string,
    name: string,
    startTimestampNs: number,
    endTimestampNs: number,
    calculatedTelemetry: boolean,
  ): Promise<unknown> {
    return this.$apiV2.get(
      `${this.getDevicesPath(organizationCode)}/${id}/telemetry/${name}?startDateTime=${startTimestampNs}&endDateTime=${endTimestampNs}&calculatedTelemetry=${calculatedTelemetry}`,
    );
  }

  async updateOrganizationDtwinLifeCycleState(
    uid: string,
    lifeCycleState: DtwinLifeCycleState,
    organizationCode?: number,
  ): Promise<void> {
    return this.$apiV2.put(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${uid}/lifeCycleState/${lifeCycleState}`,
    );
  }

  async getDeviceModelUiConfigByDeviceModelUid(
    organizationCode: number,
    deviceModelUid: string,
  ): Promise<DtwinModelUiConfig> {
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/deviceModelUIConfigs/${deviceModelUid}`,
      {
        params: { componentId: COMPONENT_ID_BS_TRANSLATION_UI_CONFIG },
      },
    );
  }

  async getOrganizationAllDeviceModelUiConfigs(
    organizationCode?: number,
  ): Promise<DtwinModelUiConfig[]> {
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/deviceModelUIConfigs`,
      {
        params: { componentId: COMPONENT_ID_BS_TRANSLATION_UI_CONFIG },
      },
    );
  }

  /**
   * Upload configuration files
   * @param files
   * @param progress
   * @param dtwinUid
   * @param organizationCode
   */
  async uploadConfigurationFiles(
    files: File[],
    progress: Ref<number | undefined | null>,
    dtwinUid: string,
    organizationCode?: number,
  ): Promise<DtwinConfigurationCreateContentResourceResponse> {
    const formData = new FormData();

    let manifestFile = undefined;

    for (const index in files) {
      const file = files[index];

      formData.append(`file${index}`, file);

      if (uploadUtils.isValidFileType(file, [".json"])) {
        manifestFile = file;
      }
    }

    const contentResource: DtwinConfigurationUploadPayloadCreateContentResource =
      {
        name: manifestFile ? manifestFile.name : "",
        deviceTwinUid: dtwinUid,
        temporary: true,
        files: files.map((file) => {
          const isManifest = file === manifestFile;

          return {
            name: file.name,
            validator: isManifest ? "configuration-manifest" : undefined,
            visible: !isManifest,
          };
        }),
        tags: [ContentTags.CONFIGURATION, ContentTags.DEVICE],
      };

    const contentResourceBlob = new Blob([JSON.stringify(contentResource)], {
      type: "application/json",
    });

    formData.append("contentResource", contentResourceBlob);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        progress.value = progressEvent.total
          ? (progressEvent.loaded / progressEvent.total) * 100
          : 0;
      },
      timeout: Infinity,
    };

    return this.$apiV4.post(
      contentService.getOrganizationContentsPath(organizationCode),
      formData,
      config,
    );
  }

  /**
   * Add or update a configuration files
   * Note: This service allow to persist the contentm ressource (temporary=false).
   *  All the attributes of the ressources cannot be updated.
   * @param contentResourceUid
   * @param descriptor
   * @param organizationCode
   */
  async updateConfigurationFiles(
    contentResourceUid: string,
    descriptor: Record<string, string>,
    organizationCode?: number,
  ): Promise<void> {
    const patchConfigurationPayload: DtwinAddConfigurationPayload = {
      descriptor,
      temporary: false,
    };

    return this.$apiV4.patch(
      `${contentService.getOrganizationContentsPath(organizationCode)}/${contentResourceUid}`,
      patchConfigurationPayload,
    );
  }

  async getDtwinsMetricsLifeCycleState(
    organizationCode?: number,
    searchParams?: string | FiltersSelection,
  ): Promise<DtwinMetricsByModelFriendlyName<DtwinMetricsCounts>> {
    const params = {
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${this.METRICS}/lifeCycleState`,
      { params },
    );
  }

  getDtwinsMetricsConnectivity(
    organizationCode?: number,
    searchParams?: string | FiltersSelection,
  ): Promise<DtwinMetricsByModelFriendlyName<DtwinMetricsCounts>> {
    const params = {
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${this.METRICS}/connectivityStatusCount`,
      { params },
    );
  }

  getDtwinsMetricsFirmwareVersion(
    organizationCode?: number,
    searchParams?: string | FiltersSelection,
  ): Promise<DtwinMetricsByModelFriendlyName<DtwinMetricsCounts>> {
    const params = {
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${this.METRICS}/firmwareVersionCountByName`,
      { params },
    );
  }

  async getDtwinsMetricsCertficateState(
    organizationCode?: number,
    searchParams?: string | FiltersSelection,
  ): Promise<DtwinsMetricsCertificate> {
    const params = {
      filter: searchParams,
    };
    return this.$apiV2.get(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${this.METRICS}/certificateStateCount`,
      { params },
    );
  }

  deleteOrganizationDtwin(
    organizationCode?: number,
    dtwinUid?: string,
  ): Promise<void> {
    return this.$apiV2.delete(
      `${this.getOrganizationDtwinsPath(organizationCode)}/${dtwinUid}`,
    );
  }

  bulkDeleteOrganizationDtwins(
    organizationCode?: number,
    dtwinUids?: string[],
  ): Promise<void> {
    return this.$apiV2.post(
      `${this.getOrganizationDtwinsPath(organizationCode)}/bulk/delete`,
      { deviceTwinUidList: dtwinUids ?? [] },
    );
  }

  private getOrganizationDtwinsPath(organizationCode?: number) {
    return `${organizationService.getOrganizationPath(organizationCode)}/${this.DTWINS}`;
  }

  private getOrganizationSectorPath(
    organizationCode?: number,
    sectorCode?: number,
  ) {
    if (!sectorCode) {
      throw new Error("Sector code is undefined");
    }
    return `${organizationService.getOrganizationPath(organizationCode)}/sectors/${sectorCode}`;
  }

  private getOrganizationSectorDtwinsPath(
    organizationCode?: number,
    sectorCode?: number,
  ) {
    return `${this.getOrganizationSectorPath(organizationCode, sectorCode)}/${this.DTWINS}`;
  }

  private getDevicesPath(organizationCode?: number) {
    return `${organizationService.getOrganizationPath(organizationCode)}/devices`;
  }
}

export default new DtwinService();
